import {
  createContext,
  ReactNode,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import axios, { AxiosInstance, CreateAxiosDefaults } from 'axios';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { isUndefined } from 'lodash-es';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { context, trace, propagation } from '@opentelemetry/api';

type SetAuthorizationBeforeRequestParams = {
  getAccessToken?: () => string;
};

type SetTraceParentBeforeRequestParams = {
  getTraceParent?: () => string;
};

type AxiosClientProviderProps = {
  axiosConfig: CreateAxiosDefaults;
  setAuthorizationBeforeRequest?: SetAuthorizationBeforeRequestParams;
  setTraceParentBeforeRequest?: SetTraceParentBeforeRequestParams;
  children: ReactNode;
};

/**
 * Request HeadersのAuthorizationにAccess Tokenを付与します
 * @param client Axiosインスタンス
 * @param params SetAuthorizationのパラメタ
 */
function useSetAuthorizationBeforeRequest(
  client?: AxiosInstance,
  params?: SetAuthorizationBeforeRequestParams
) {
  const { accessToken } = useOidcAccessToken();

  useEffect(() => {
    if (isUndefined(client) || isUndefined(params)) {
      return;
    }
    const interceptor = client.interceptors.request.use(
      (config) => {
        config.headers.set('Authorization', `Bearer ${accessToken}`, true);
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      client.interceptors.request.eject(interceptor);
    };
  }, [client, params, accessToken]);
}

/**
 * W3C traceContextのtraceparent: exp)00-0a57cd57c2bc783115c1fcac4f09d93a-078e37524e137d15-01を生成
 * @access private
 * @returns traceparentを取得するデフォルト関数
 */
function useGetTraceParentDefault(): () => string {
  const getTraceParentDefault = useCallback(() => {
    const provider = new WebTracerProvider();
    provider.register({
      contextManager: new ZoneContextManager(),
    });

    const webTracerWithZone = provider.getTracer('');

    const output: { [key: string]: string } = {};
    const singleSpan = webTracerWithZone.startSpan('');

    context.with(trace.setSpan(context.active(), singleSpan), () => {
      propagation.inject(context.active(), output);
    });

    singleSpan.end();

    return output.traceparent;
  }, []);

  return getTraceParentDefault;
}

/**
 * axiosにInterceptorsを使ってHttpRequestHeadersにTraceparentを設定
 * @param client Axiosインスタンス
 * @param params SetTraceParentのパラメタ
 */
function useSetTraceParentBeforeRequest(
  client?: AxiosInstance,
  params?: SetTraceParentBeforeRequestParams
) {
  const getTraceParentDefault = useGetTraceParentDefault();

  useEffect(() => {
    if (isUndefined(client) || isUndefined(params)) {
      return;
    }
    const getTraceParent = params.getTraceParent ?? getTraceParentDefault;
    client.interceptors.request.use(
      (requestConfig) => {
        const updatedConfig = requestConfig;
        updatedConfig.headers.Traceparent = getTraceParent();
        return updatedConfig;
      },
      (error) => Promise.reject(error)
    );
  }, [client, params, getTraceParentDefault]);
}

type AxiosClient = AxiosInstance;

const AxiosClientContext = createContext<AxiosClient>({} as AxiosClient);

export function AxiosClientProvider({
  axiosConfig,
  setAuthorizationBeforeRequest,
  setTraceParentBeforeRequest,
  children,
}: AxiosClientProviderProps): ReactElement | null {
  const [client, setClient] = useState<AxiosClient | undefined>(undefined);

  useEffect(() => {
    setClient(() => axios.create(axiosConfig));
  }, [axiosConfig]);

  useSetAuthorizationBeforeRequest(client, setAuthorizationBeforeRequest);

  useSetTraceParentBeforeRequest(client, setTraceParentBeforeRequest);

  if (isUndefined(client)) {
    return null;
  }

  return (
    <AxiosClientContext.Provider value={client}>
      {children}
    </AxiosClientContext.Provider>
  );
}

export const useAxiosClient = () => {
  const client = useContext(AxiosClientContext);

  return client;
};
